import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { FileReference } from '../file-reference/file-reference.js';
import { LocalizedString } from '../localized-string.js';

export class TicketEmailOverride extends DataModel {
  static readonly TYPE_ID: string = '0aec0978aa384b3cad6b3afd8ea4f3e0';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
    public readonly enabled: boolean,
    public readonly body: LocalizedString,
    public readonly supplementaryEmailAttachments: FileReference[],
  ) {
    super();
  }

  get $typeId() {
    return TicketEmailOverride.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TicketEmailOverride.SCHEMA_VERSION);
  }

  static create(options: {
    enabled: boolean,
    body: LocalizedString,
    supplementaryEmailAttachments: FileReference[],
  }): TicketEmailOverride {
    return new TicketEmailOverride(
        options.enabled,
        options.body,
        options.supplementaryEmailAttachments,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): TicketEmailOverride {
    return new TicketEmailOverride(
        MapUtils.getBoolean(map, 'enabled'),
        context.fromData<LocalizedString>(MapUtils.getRaw(map, 'body') as SerializedData),
        MapUtils.getList<FileReference>(
            map,
            'supplementaryEmailAttachments',
            (value: unknown) => context.fromData<FileReference>(value as SerializedData),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'enabled': this.enabled,
      'body': context.toData(this.body),
      'supplementaryEmailAttachments': this.supplementaryEmailAttachments.map((p) => context.toData(p)),
    };
  }

  clone(): this {
    return new TicketEmailOverride(
        this.enabled,
        this.body.clone(),
        this.supplementaryEmailAttachments.map((e) => e.clone()),
    ) as this;
  }

  isSame(other: TicketEmailOverride): boolean {
    if(other === this) {
      return true;
    }

    return other.enabled === this.enabled &&
      isSameData(other.body, this.body) &&
      isSameList(other.supplementaryEmailAttachments, this.supplementaryEmailAttachments, isSameData);
  }
}

export class TicketEmailOverrideBuilder extends DataModelBuilder<TicketEmailOverride> {
  build(context: SerializationContext, map: SerializedData): TicketEmailOverride {
    return TicketEmailOverride.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TicketEmailOverride.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(TicketEmailOverride.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      version = 2;
      map['supplementaryEmailAttachments'] = [];
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
